import React, { useEffect, useRef, useState } from 'react';
import { CircularProgress, Tooltip } from '@mui/material';
import LayersIcon from '@mui/icons-material/Layers';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import type {
  AceBulkFilter,
  AceTariffMatch,
  AceTariffMatchMode,
} from 'api/AnalyzersAPI';

// Small hover-help affordance — an info glyph carrying a tooltip. Used
// next to section headers and controls so first-time users can learn what
// each part does without extra on-screen prose bloating the modal.
const HelpIcon: React.FC<{ text: React.ReactNode }> = ({ text }) => (
  <Tooltip title={text} arrow placement='top'>
    <InfoOutlinedIcon
      sx={{
        fontSize: 14,
        color: '#98a2b3',
        cursor: 'help',
        verticalAlign: 'middle',
      }}
    />
  </Tooltip>
);

// Plain-language help copy, kept in one place. JSX where a little
// structure helps legibility (the match-mode glossary).
const HELP = {
  combine:
    'Transactions must match every category you fill in (tariff AND finding AND country). Within one category, any selected option counts.',
  tariff:
    'Target transactions by tariff program. For each program you check, use the toggle on the right to say how it must relate to the transaction.',
  modes: (
    <span style={{ fontSize: '12px', lineHeight: 1.5 }}>
      <b>Applied</b> — the program is already on the filed entry.
      <br />
      <b>Recommends</b> — BITE expected the program, but it isn&apos;t on the
      entry.
      <br />
      <b>Either</b> — matches when the program is applied or recommended.
    </span>
  ),
  findings:
    'Target transactions that have at least one of the selected findings. Leave empty to match regardless of findings.',
  countries:
    'Target transactions whose country of origin matches one of the selected codes.',
  actions: {
    clear:
      'Dismiss findings on the matched transactions so they no longer count as needing review. Non-destructive — you can restore them later.',
    restore:
      'Bring back findings you previously cleared on the matched transactions.',
    comment: 'Attach a note to every matched transaction.',
    review:
      'Mark the matched transactions as reviewed (green status) without changing their findings.',
    unreview: 'Remove the reviewed status from the matched transactions.',
  } as Record<BulkAction, string>,
  applyTo:
    'Choose whether the action hits every finding on a matched transaction, or only the finding types you selected above.',
};

// Same catalogs the UnifiedFilterModal uses. Kept duplicated rather than
// shared because the two modals serve different purposes — one filters
// the table view, one applies destructive-feeling actions — and might
// diverge (different labels, different option sets). When the master
// catalog changes, update both.
const TARIFF_OPTIONS = [
  { key: 'ch98_hts2', label: 'Chapter 98' },
  { key: 'sec_301', label: 'Section 301' },
  { key: 'sec_301_flip', label: 'Section 301 Flip' },
  { key: 'sec_232_auto', label: 'Sec 232 Auto' },
  { key: 'sec_232_mhdv', label: 'Sec 232 MHDV' },
  { key: 'sec_232_steel', label: 'Sec 232 Steel' },
  { key: 'sec_232_alum', label: 'Sec 232 Aluminum' },
  { key: 'sec_232_copper', label: 'Sec 232 Copper' },
  { key: 'sec_232_consol_metal', label: 'Sec 232 Consol. Metal' },
  { key: 'sec_232_sw_timber', label: 'Sec 232 Softwood Timber' },
  { key: 'sec_232_semiconductor', label: 'Sec 232 Semiconductor' },
  { key: 'sec_232_pharma', label: 'Sec 232 Pharma' },
  { key: 'sec_232_uas', label: 'Sec 232 UAS' },
  { key: 'ieepa_co_specific', label: 'IEEPA Country Specific' },
  { key: 'ieepa_reciprocal', label: 'IEEPA Reciprocal' },
  { key: 'sec_122', label: 'Section 122' },
  { key: 'sec_338', label: 'Section 338' },
  { key: 'sec_201_qsp', label: 'Sec 201 QSP' },
  { key: 'civil_air', label: 'Civil Aircraft' },
  { key: 'other_ch99', label: 'Other Chapter 99' },
];

const FINDING_OPTIONS = [
  'Missing Data',
  'BITE Warning',
  'Missing Tariff Quantity',
  'Missing Ch. 99',
  'Unexpected Ch. 99',
  'Base Duty Discrepancy',
  'Tariff Ordering',
  'Needs Tariff Tool Review',
  'Metal Content Exceeds Line',
  'Ineligible FTA',
  'Potential AD/CVD',
  'Data Error',
  'Processing Error',
  'Tariff Duty Discrepancy',
  'Missing Fees',
  'Extra Fees',
];

const MATCH_MODES: { key: AceTariffMatchMode; label: string }[] = [
  { key: 'applied', label: 'Applied' },
  { key: 'recommends', label: 'Recommends' },
  { key: 'either', label: 'Either' },
];

export type BulkAction = 'clear' | 'restore' | 'comment' | 'review' | 'unreview';
export type ClearScope = 'all' | 'selected';

interface BulkActionsModalProps {
  handleClose: () => void;
  availableTariffs: string[];
  availableFindings: string[];
  availableCountries: string[];
  /**
   * Called when the user confirms a bulk action. Parent runs the API call
   * and shows a success/error snackbar. Modal stays mounted while
   * isLoading is true so the user sees the spinner.
   */
  onSubmit: (params: {
    action: BulkAction;
    filter: AceBulkFilter;
    clearScope: ClearScope;
    commentText?: string;
  }) => Promise<void>;
  /**
   * Optional read-only preview — given the current criteria, resolves to
   * how many transactions would be affected. Drives the footer count.
   * Debounced by the modal; the parent just runs the count query.
   */
  onPreview?: (filter: AceBulkFilter) => Promise<number>;
  /**
   * Plain-language summary of the table's active tab / search / filters, when
   * any are set. The action is restricted to those rows on top of the criteria
   * chosen here, so it is stated outright — a bulk action that quietly affects
   * a different set than the operator expects is the failure mode worth
   * designing against. Empty when the table is unfiltered.
   */
  scopeDescription?: string;
  isLoading: boolean;
}

const BulkActionsModal: React.FC<BulkActionsModalProps> = ({
  handleClose,
  availableTariffs,
  availableFindings,
  availableCountries,
  onSubmit,
  onPreview,
  scopeDescription,
  isLoading,
}) => {
  // Tariff selection is a map prefix → match mode. A prefix in the map is
  // selected; its value is how it's matched. Absent = unselected.
  const [tariffModes, setTariffModes] = useState<Record<string, AceTariffMatchMode>>(
    {},
  );
  const [findings, setFindings] = useState<string[]>([]);
  const [countries, setCountries] = useState<string[]>([]);
  const [action, setAction] = useState<BulkAction>('clear');
  const [clearScope, setClearScope] = useState<ClearScope>('selected');
  const [commentText, setCommentText] = useState<string>('');

  const [previewCount, setPreviewCount] = useState<number | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  const visibleTariffs = TARIFF_OPTIONS.filter((opt) =>
    availableTariffs.includes(opt.key),
  );
  // Catalog order first, then any type present on the run that the catalog
  // doesn't list — appended instead of dropped so a newly-added processor
  // flag is always targetable here (see the same note in UnifiedFilterModal).
  const visibleFindings = FINDING_OPTIONS.filter((f) =>
    availableFindings.includes(f),
  ).concat(
    (availableFindings || [])
      .filter((f) => f && FINDING_OPTIONS.indexOf(f) === -1)
      .sort(),
  );
  const visibleCountries = (availableCountries || []).slice().sort();

  // Clicking the checkbox toggles selection; first selection defaults to
  // 'either' (the most inclusive — "cover all transactions with X").
  const toggleTariff = (prefix: string) => {
    setTariffModes((prev) => {
      const next = { ...prev };
      if (next[prefix]) delete next[prefix];
      else next[prefix] = 'either';
      return next;
    });
  };

  // Clicking a mode segment both selects the program (if not already) and
  // sets that mode — so one click on "Applied" checks the row in Applied.
  const setTariffMode = (prefix: string, mode: AceTariffMatchMode) => {
    setTariffModes((prev) => ({ ...prev, [prefix]: mode }));
  };

  const toggle = (
    list: string[],
    setList: React.Dispatch<React.SetStateAction<string[]>>,
    value: string,
  ) => {
    setList(
      list.includes(value) ? list.filter((v) => v !== value) : [...list, value],
    );
  };

  const tariffMatches: AceTariffMatch[] = Object.entries(tariffModes).map(
    ([prefix, mode]) => ({ prefix: prefix, mode: mode }),
  );

  const buildFilter = (): AceBulkFilter => ({
    tariffMatches: tariffMatches,
    flagTypes: findings,
    countryCodes: countries,
  });

  const hasCriteria =
    tariffMatches.length > 0 || findings.length > 0 || countries.length > 0;

  const scopeApplies = action === 'clear' || action === 'restore';
  // 'selected' only means something when finding types are chosen; with
  // none selected the scope collapses to 'all' so the action is never
  // stuck. This is what actually gets sent + labeled.
  const effectiveScope: ClearScope = findings.length === 0 ? 'all' : clearScope;
  const isCommentReady = action !== 'comment' || commentText.trim().length > 0;
  const canSubmit = hasCriteria && isCommentReady && !isLoading;

  // Live preview of the affected-row count. Debounced so dragging through
  // checkboxes doesn't spray the endpoint. Serialize the criteria into the
  // dep string so the effect re-runs on any change. A per-run token guards
  // against out-of-order responses.
  const criteriaKey = JSON.stringify({
    tariffModes: tariffModes,
    findings: findings,
    countries: countries,
  });
  const previewToken = useRef(0);
  useEffect(() => {
    if (!onPreview || !hasCriteria) {
      // Bump the token so any request already in flight (started while
      // criteria WERE set) can't resolve and repaint a stale count after
      // the user clears everything.
      previewToken.current += 1;
      setPreviewCount(null);
      setPreviewLoading(false);
      return undefined;
    }
    const token = ++previewToken.current;
    setPreviewLoading(true);
    const handle = window.setTimeout(() => {
      onPreview(buildFilter())
        .then((count) => {
          if (previewToken.current === token) {
            setPreviewCount(count);
            setPreviewLoading(false);
          }
        })
        .catch(() => {
          if (previewToken.current === token) {
            setPreviewCount(null);
            setPreviewLoading(false);
          }
        });
    }, 400);
    return () => window.clearTimeout(handle);
  }, [criteriaKey]);

  const handleSubmit = async () => {
    if (!canSubmit) return;
    await onSubmit({
      action: action,
      filter: buildFilter(),
      clearScope: effectiveScope,
      commentText: action === 'comment' ? commentText.trim() : undefined,
    });
  };

  // Per-action button label + color. Clear/Restore fold the scope into the
  // label so the destructive intent is unambiguous.
  const scopeWord = effectiveScope === 'selected' ? 'selected' : 'all';
  const actionMeta: Record<BulkAction, { label: string; color: string }> = {
    clear: { label: `Clear ${scopeWord} findings`, color: '#f04438' },
    restore: { label: `Restore ${scopeWord} findings`, color: '#10b981' },
    comment: { label: 'Add comment', color: '#4895EF' },
    review: { label: 'Mark reviewed', color: '#12b76a' },
    unreview: { label: 'Unmark reviewed', color: '#f59e0b' },
  };
  const actionLabel = actionMeta[action].label;
  const actionColor = actionMeta[action].color;

  const ACTION_PILLS: { key: BulkAction; label: string; color: string }[] = [
    { key: 'clear', label: 'Clear findings', color: '#f04438' },
    { key: 'restore', label: 'Restore findings', color: '#10b981' },
    { key: 'comment', label: 'Add comment', color: '#4895EF' },
    { key: 'review', label: 'Mark reviewed', color: '#12b76a' },
    { key: 'unreview', label: 'Unmark reviewed', color: '#f59e0b' },
  ];

  const sectionLabelStyle: React.CSSProperties = {
    fontSize: '12px',
    fontWeight: 600,
    color: '#667085',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
  };

  return (
    <div
      className='audit-add-modal-cont'
      // Cap the whole modal at the viewport height and lay it out as a
      // flex column so the header + action/footer stay pinned while the
      // criteria body is the one scrolling region. Without this the modal
      // grows past a short screen and — being centered with no outer
      // scroll — clips its own top/bottom.
      style={{
        maxWidth: '1100px',
        width: '100%',
        maxHeight: '88vh',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div className='audit-add-modal-cont-head'>
        <div className='audit-add-modal-head-cont-change'>
          <div className='audit-add-modal-head-cont-icon'>
            <LayersIcon sx={{ color: '#4895EF', width: '25px', height: '24px' }} />
          </div>
          <div className='audit-add-modal-head-cont-supporting'>
            <h1>Bulk Actions</h1>
            <p>
              Pick which transactions to target, then choose what to do with
              them. <HelpIcon text={HELP.combine} />
            </p>
          </div>
        </div>
        <div
          className='audit-add-modal-head-close'
          onClick={handleClose}
          style={{ cursor: 'pointer' }}
        >
          <svg
            xmlns='http://www.w3.org/2000/svg'
            width='24'
            height='24'
            viewBox='0 0 24 24'
            fill='none'
          >
            <path
              d='M18 6L6 18M6 6L18 18'
              stroke='#85888E'
              strokeWidth='2'
              strokeLinecap='round'
              strokeLinejoin='round'
            />
          </svg>
        </div>
      </div>

      <div className='activity-cont-frame-left-table-feed-row-cont-divider'></div>

      <div
        className='audit-add-modal-cont-body'
        // flex:1 + minHeight:0 lets this region absorb the modal's height
        // cap and scroll internally; the header/footer siblings keep their
        // natural height. minHeight:0 is required for a flex child to be
        // allowed to shrink below its content and actually scroll.
        style={{
          paddingTop: '20px',
          paddingBottom: '20px',
          flex: '1 1 auto',
          minHeight: 0,
          overflowY: 'auto',
        }}
      >
        {/* --- Tariff programs — full width so each row carries its own
            Applied / Recommends / Either match mode. --- */}
        {/* --- Criteria: three inline columns so the picker stays compact.
            Tariff Programs gets ~2× width to fit its per-program match-mode
            control; findings + country sit beside it. A reserved (hidden)
            subtitle line on the shorter columns keeps all three lists top-
            aligned with the tariff column's "match each on…" hint. --- */}
        <div
          style={{
            display: 'flex',
            gap: '28px',
            width: '100%',
            paddingLeft: '24px',
            paddingRight: '24px',
            marginBottom: '24px',
            alignItems: 'flex-start',
          }}
        >
          {visibleTariffs.length > 0 && (
            <div style={{ flex: '2 1 0', minWidth: 0 }}>
              <div style={{ marginBottom: '10px' }}>
                <h3
                  style={{
                    ...sectionLabelStyle,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                  }}
                >
                  Tariff Programs <HelpIcon text={HELP.tariff} />
                </h3>
                <p
                  style={{
                    margin: '4px 0 0',
                    fontSize: '11px',
                    color: '#98a2b3',
                    fontStyle: 'italic',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                  }}
                >
                  match each on: applied · recommends · either{' '}
                  <HelpIcon text={HELP.modes} />
                </p>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                {visibleTariffs.map((opt) => {
                  const selected = !!tariffModes[opt.key];
                  const mode = tariffModes[opt.key];
                  return (
                    <div
                      key={opt.key}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        padding: '4px 8px',
                        borderRadius: '6px',
                        background: selected
                          ? 'var(--bg-surface, #1f2937)'
                          : 'transparent',
                      }}
                    >
                      <div
                        className='bill-container-fit-screen-content-row-checkbox'
                        style={{ flex: 1, minWidth: 0, cursor: 'pointer' }}
                        onClick={() => toggleTariff(opt.key)}
                      >
                        <div className='bill-container-fit-screen-content-row-input'>
                          <div
                            className={`bill-container-fit-screen-content-row-input-icon ${
                              selected ? 'selected' : ''
                            }`}
                          >
                            <div
                              className={`bill-container-fit-screen-content-row-input-icon-rnd ${
                                selected ? 'selected' : ''
                              }`}
                            />
                          </div>
                        </div>
                        <div className='bill-container-fit-screen-content-row-checkbox-text'>
                          <p style={{ margin: 0 }}>{opt.label}</p>
                        </div>
                      </div>

                      {/* Segmented match-mode control. Dimmed until the row
                          is selected; clicking a segment selects + sets it. */}
                      <div
                        style={{
                          display: 'flex',
                          border: '1px solid var(--es-soft-border, #344054)',
                          borderRadius: '6px',
                          overflow: 'hidden',
                          opacity: selected ? 1 : 0.5,
                          flex: 'none',
                        }}
                      >
                        {MATCH_MODES.map((m, i) => {
                          const active = selected && mode === m.key;
                          return (
                            <div
                              key={m.key}
                              onClick={() => setTariffMode(opt.key, m.key)}
                              style={{
                                padding: '4px 12px',
                                fontSize: '11px',
                                fontWeight: active ? 600 : 400,
                                cursor: 'pointer',
                                whiteSpace: 'nowrap',
                                color: active ? '#fff' : '#667085',
                                background: active ? '#4895EF' : 'transparent',
                                borderLeft:
                                  i > 0
                                    ? '1px solid var(--es-soft-border, #344054)'
                                    : 'none',
                              }}
                            >
                              {m.label}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {visibleFindings.length > 0 && (
            <div style={{ flex: '1 1 0', minWidth: 0 }}>
              <div style={{ marginBottom: '10px' }}>
                <h3
                  style={{
                    ...sectionLabelStyle,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                  }}
                >
                  Discrepancies &amp; Flags <HelpIcon text={HELP.findings} />
                </h3>
                <p
                  aria-hidden='true'
                  style={{ margin: '4px 0 0', fontSize: '11px', visibility: 'hidden' }}
                >
                  spacer
                </p>
              </div>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '12px',
                  maxHeight: '340px',
                  overflowY: 'auto',
                  paddingRight: '4px',
                }}
              >
                {visibleFindings.map((f) => (
                  <div
                    key={f}
                    className='bill-container-fit-screen-content-row-checkbox'
                    onClick={() => toggle(findings, setFindings, f)}
                  >
                    <div className='bill-container-fit-screen-content-row-input'>
                      <div
                        className={`bill-container-fit-screen-content-row-input-icon ${
                          findings.includes(f) ? 'selected' : ''
                        }`}
                      >
                        <div
                          className={`bill-container-fit-screen-content-row-input-icon-rnd ${
                            findings.includes(f) ? 'selected' : ''
                          }`}
                        />
                      </div>
                    </div>
                    <div className='bill-container-fit-screen-content-row-checkbox-text'>
                      <p style={{ margin: 0 }}>{f}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {visibleCountries.length > 0 && (
            <div style={{ flex: '1 1 0', minWidth: 0 }}>
              <div style={{ marginBottom: '10px' }}>
                <h3
                  style={{
                    ...sectionLabelStyle,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                  }}
                >
                  Country of Origin <HelpIcon text={HELP.countries} />
                </h3>
                <p
                  aria-hidden='true'
                  style={{ margin: '4px 0 0', fontSize: '11px', visibility: 'hidden' }}
                >
                  spacer
                </p>
              </div>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '12px',
                  maxHeight: '340px',
                  overflowY: 'auto',
                  paddingRight: '4px',
                }}
              >
                {visibleCountries.map((iso) => (
                  <div
                    key={iso}
                    className='bill-container-fit-screen-content-row-checkbox'
                    onClick={() => toggle(countries, setCountries, iso)}
                  >
                    <div className='bill-container-fit-screen-content-row-input'>
                      <div
                        className={`bill-container-fit-screen-content-row-input-icon ${
                          countries.includes(iso) ? 'selected' : ''
                        }`}
                      >
                        <div
                          className={`bill-container-fit-screen-content-row-input-icon-rnd ${
                            countries.includes(iso) ? 'selected' : ''
                          }`}
                        />
                      </div>
                    </div>
                    <div className='bill-container-fit-screen-content-row-checkbox-text'>
                      <p style={{ margin: 0 }}>{iso}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* --- Action picker + per-action scope / comment. --- */}
        <div style={{ padding: '0 24px' }}>
          <h3
            style={{
              ...sectionLabelStyle,
              marginBottom: '12px',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
            }}
          >
            Action{' '}
            <HelpIcon text='What to do with the transactions matched by the criteria above. Hover any option to see what it does.' />
          </h3>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            {ACTION_PILLS.map((opt) => {
              const selected = action === opt.key;
              return (
                <Tooltip
                  key={opt.key}
                  title={HELP.actions[opt.key]}
                  arrow
                  placement='top'
                >
                  <button
                    onClick={() => setAction(opt.key)}
                    style={{
                      border: `1px solid ${opt.color}`,
                      color: selected ? '#fff' : opt.color,
                      background: selected ? opt.color : 'transparent',
                      borderRadius: '6px',
                      padding: '8px 16px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: 500,
                    }}
                  >
                    {opt.label}
                  </button>
                </Tooltip>
              );
            })}
          </div>

          {scopeApplies && (
            <div
              style={{
                marginTop: '12px',
                padding: '12px 14px',
                background: 'var(--bg-surface, #1f2937)',
                borderRadius: '6px',
              }}
            >
              <p
                style={{
                  margin: '0 0 8px',
                  fontSize: '12px',
                  fontWeight: 600,
                  color: '#667085',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                }}
              >
                Apply to <HelpIcon text={HELP.applyTo} />
              </p>
              {(
                [
                  {
                    key: 'selected' as ClearScope,
                    label:
                      findings.length > 0
                        ? `Only the finding types selected above (${findings.length})`
                        : 'Only the finding types selected above',
                    disabled: findings.length === 0,
                  },
                  {
                    key: 'all' as ClearScope,
                    label:
                      action === 'clear'
                        ? 'All findings on matched transactions'
                        : 'All cleared findings on matched transactions',
                    disabled: false,
                  },
                ] as const
              ).map((opt) => {
                const checked = effectiveScope === opt.key;
                return (
                  <div
                    key={opt.key}
                    onClick={() => !opt.disabled && setClearScope(opt.key)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '4px 0',
                      cursor: opt.disabled ? 'not-allowed' : 'pointer',
                      opacity: opt.disabled ? 0.45 : 1,
                    }}
                  >
                    <span
                      style={{
                        width: '15px',
                        height: '15px',
                        borderRadius: '50%',
                        flex: 'none',
                        border: checked
                          ? '4px solid #4895EF'
                          : '1px solid var(--es-soft-border, #344054)',
                        background: 'var(--bg-colors, #101828)',
                        boxSizing: 'border-box',
                      }}
                    />
                    <span
                      style={{
                        fontSize: '13px',
                        color: 'var(--text-primary-color)',
                      }}
                    >
                      {opt.label}
                    </span>
                  </div>
                );
              })}
            </div>
          )}

          {action === 'comment' && (
            <textarea
              value={commentText}
              onChange={(e) => setCommentText(e.target.value)}
              placeholder='Comment to add to every matching row…'
              rows={3}
              style={{
                marginTop: '12px',
                width: '100%',
                padding: '10px 12px',
                borderRadius: '6px',
                border: '1px solid var(--es-soft-border)',
                background: 'var(--bg-secondary, #1f2937)',
                color: 'inherit',
                fontFamily: 'inherit',
                fontSize: '14px',
                resize: 'vertical',
                boxSizing: 'border-box',
              }}
            />
          )}
        </div>
      </div>

      <div className='activity-cont-frame-left-table-feed-row-cont-divider'></div>

      <div className='audit-modal-cont-footer'>
        <div
          className='audit-modal-cont-footer-content'
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            width: '100%',
            alignItems: 'center',
          }}
        >
          <div
            style={{
              fontSize: '12px',
              color: '#667085',
              paddingLeft: '24px',
            }}
          >
            {/* Stated before the count, because it explains it: the number
                below is already narrowed to these rows. */}
            {scopeDescription ? (
              <div
                style={{
                  marginBottom: '4px',
                  color: 'var(--text-primary-color)',
                  fontWeight: 500,
                }}
              >
                Limited to the current view — {scopeDescription}
              </div>
            ) : null}
            {!hasCriteria ? (
              <span style={{ fontStyle: 'italic' }}>
                Select at least one criterion to enable the action.
              </span>
            ) : previewLoading ? (
              'Counting matching transactions…'
            ) : previewCount !== null ? (
              <span>
                Will affect{' '}
                <span style={{ fontWeight: 600, color: 'var(--text-primary-color)' }}>
                  {previewCount.toLocaleString()}
                </span>{' '}
                transaction{previewCount === 1 ? '' : 's'}.
              </span>
            ) : (
              'Action applies to every transaction matching the criteria above.'
            )}
          </div>
          <div
            className='audit-modal-cont-footer-content-btn-upload'
            onClick={canSubmit ? handleSubmit : undefined}
            // Override both background AND border so the pill matches the
            // chosen action's color. The base class always paints a blue
            // border; without an inline border the red Clear button reads
            // "blue-bordered red" which misleads users about intent.
            style={{
              width: 'auto',
              padding: '0 24px',
              flex: 'none',
              opacity: canSubmit ? 1 : 0.5,
              cursor: canSubmit ? 'pointer' : 'not-allowed',
              background: actionColor,
              border: `1px solid ${actionColor}`,
            }}
          >
            <div
              className='audit-modal-cont-footer-content-btn-text'
              style={{
                color: '#fff',
                display: 'flex',
                alignItems: 'center',
                whiteSpace: 'nowrap',
              }}
            >
              {isLoading && (
                <CircularProgress
                  style={{ width: '16px', height: '16px', marginRight: '8px' }}
                  color='inherit'
                />
              )}
              {actionLabel}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BulkActionsModal;
